<script setup lang="ts">
  // Presentational clone of the desktop V2PrinterCard over the native merged
  // card model: transport icon, honest per-transport status badge,
  // assignment badges, last-job line and the kebab menu. All data arrives
  // via the card prop — no store access here.
  import { computed } from 'vue';
  import {
    INativePrinterCard,
    TNativeTestKind,
    nativeCardStatusUi,
    nativeTransportLabelKey,
  } from '@/composition/printing/use-native-printing-ui';
  import { formatJobTime } from '@/composition/printing/use-printing-v2-ui';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const props = defineProps<{
    card: INativePrinterCard;
    /** Test dispatch kind — 'ambiguous' renders the two explicit items. */
    testKind: TNativeTestKind;
    /** This card's printer is currently testing (spinner). */
    testing: boolean;
    /** ANY printer is testing (module-scope guard) — disable test actions. */
    anyTesting: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'test', kindOverride?: 'escpos' | 'zpl'): void;
    (e: 'edit'): void;
    (e: 'remove'): void;
    (e: 'unassign'): void;
  }>();

  const transportIcon = computed(() => {
    if (props.card.isGhost) return 'printer-off';
    if (props.card.transport === 'usb') return 'usb';
    if (props.card.transport === 'bluetooth') return 'bluetooth';
    return 'lan';
  });

  // Shared pure helpers (also drive the Help-tab diagnostics block).
  const transportLabelKey = computed(() =>
    nativeTransportLabelKey(props.card.transport)
  );
  const statusUi = computed(() => nativeCardStatusUi(props.card));

  const subtitle = computed(() => {
    if (props.card.isGhost) return '';
    if (props.card.networkConfig) {
      return `${props.card.networkConfig.host}:${props.card.networkConfig.port}`;
    }
    return props.card.printer?.id ?? '';
  });

  const lastJobTime = computed(() =>
    props.card.lastJob
      ? formatJobTime(new Date(props.card.lastJob.createdAt).getTime())
      : ''
  );

  /** 3-state last-job line: printed / unknown ("Verificar") / failed. */
  const lastJobUi = computed(() => {
    const status = props.card.lastJob?.status;
    if (status === 'printed') {
      return { icon: 'check', cssClass: '', labelKey: 'printingV2.jobPrinted' };
    }
    if (status === 'unknown') {
      return {
        icon: 'help',
        cssClass: 'is-verify',
        labelKey: 'printingV2.jobVerify',
      };
    }
    return {
      icon: 'close',
      cssClass: 'is-fail',
      labelKey: 'printingV2.jobFailed',
    };
  });
</script>

<template>
  <div class="v2-native-printer-card" :class="{ 'is-ghost': card.isGhost }">
    <div class="card-top">
      <div class="card-transport-icon">
        <PosUiIcon :name="transportIcon" :size="22" />
      </div>

      <div class="card-heading">
        <div class="card-name">{{ card.name }}</div>
        <div v-if="!card.isGhost" class="card-subtitle">
          {{ $t(transportLabelKey) }}
          <template v-if="subtitle"> · {{ subtitle }}</template>
        </div>
        <div v-else class="card-subtitle">
          {{ $t('printingV2.nativeGhostHint') }}
        </div>
      </div>

      <span v-if="testing" class="card-testing-spinner" />
      <v-menu v-else offset-y left content-class="v2-native-printer-card-menu">
        <template #activator="{ on, attrs }">
          <button
            class="card-menu-button"
            type="button"
            v-bind="attrs"
            v-on="on"
          >
            <PosUiIcon name="dots-vertical" :size="20" />
          </button>
        </template>
        <div class="menu-panel">
          <template v-if="!card.isGhost">
            <!-- Ambiguous (unassigned): the user picks the format explicitly
                 instead of a blind ESC/POS a ZPL-only printer would eat. -->
            <template v-if="testKind === 'ambiguous'">
              <button
                class="menu-item"
                type="button"
                :disabled="anyTesting"
                @click="emit('test', 'escpos')"
              >
                <PosUiIcon name="receipt" class="menu-item-icon" />
                {{ $t('printingV2.nativeTestAsTicket') }}
              </button>
              <button
                class="menu-item"
                type="button"
                :disabled="anyTesting"
                @click="emit('test', 'zpl')"
              >
                <PosUiIcon name="tag-outline" class="menu-item-icon" />
                {{ $t('printingV2.nativeTestAsLabel') }}
              </button>
            </template>
            <button
              v-else
              class="menu-item"
              type="button"
              :disabled="anyTesting"
              @click="emit('test')"
            >
              <PosUiIcon name="printer-check" class="menu-item-icon" />
              {{ $t('printingV2.menuTest') }}
            </button>
            <button class="menu-item" type="button" @click="emit('edit')">
              <PosUiIcon name="pencil" class="menu-item-icon" />
              {{ $t('printingV2.menuEdit') }}
            </button>
            <button
              v-if="card.networkConfig"
              class="menu-item is-danger"
              type="button"
              @click="emit('remove')"
            >
              <PosUiIcon name="delete-outline" class="menu-item-icon" />
              {{ $t('printingV2.menuRemove') }}
            </button>
          </template>
          <button
            v-else
            class="menu-item is-danger"
            type="button"
            @click="emit('unassign')"
          >
            <PosUiIcon name="link-off" class="menu-item-icon" />
            {{ $t('printingV2.nativeRemoveAssignments') }}
          </button>
        </div>
      </v-menu>
    </div>

    <div class="card-badges">
      <PosUiBadge
        v-if="statusUi"
        :color="statusUi.color"
        variant="soft"
        size="sm"
      >
        {{ $t(statusUi.labelKey) }}
      </PosUiBadge>
      <!-- DLE-EOT paper flag (probe answered): shown ALONGSIDE the online
           chip — the printer is reachable, it just needs paper. -->
      <PosUiBadge
        v-if="card.paperOut"
        color="warning"
        variant="soft"
        size="sm"
        icon="alert"
      >
        {{ $t('printingV2.statusPaperOut') }}
      </PosUiBadge>
      <PosUiBadge
        v-for="assignment in card.assignments"
        :key="assignment.key"
        color="info"
        variant="soft"
        size="sm"
      >
        <template v-if="assignment.destination === 'ticket'">
          {{ $t('printingV2.destTicket') }}
        </template>
        <template v-else-if="assignment.destination === 'sticker'">
          {{ $t('printingV2.destSticker') }}
        </template>
        <template v-else>{{ assignment.label }}</template>
      </PosUiBadge>
    </div>

    <div v-if="card.lastJob" class="card-last-job">
      <PosUiIcon
        :name="lastJobUi.icon"
        class="card-last-job-icon"
        :class="lastJobUi.cssClass"
      />
      {{ $t('printingV2.lastJob', { when: lastJobTime }) }} —
      {{ $t(lastJobUi.labelKey) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
  .v2-native-printer-card {
    @apply tw-flex tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white tw-p-4 tw-shadow-sm;

    &.is-ghost {
      @apply tw-border-dashed tw-bg-neutral-50/60;
    }

    .card-top {
      @apply tw-flex tw-items-start tw-gap-3;

      .card-transport-icon {
        @apply tw-flex tw-h-10 tw-w-10 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-neutral-500;
      }

      .card-heading {
        @apply tw-min-w-0 tw-flex-1;

        .card-name {
          @apply tw-truncate tw-text-sm tw-font-semibold tw-text-neutral-900;
        }

        .card-subtitle {
          @apply tw-text-xs tw-text-neutral-500;
        }
      }

      .card-menu-button {
        @apply tw-flex tw-h-8 tw-w-8 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-neutral-400 tw-transition-colors hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
      }

      .card-testing-spinner {
        @apply tw-mt-1.5 tw-h-5 tw-w-5 tw-flex-none tw-animate-spin tw-rounded-full tw-border-2 tw-border-neutral-300 tw-border-t-orange-500;
      }
    }

    .card-badges {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5;
    }

    .card-last-job {
      @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-neutral-500;

      .card-last-job-icon {
        @apply tw-text-sm tw-text-emerald-600;

        &.is-fail {
          @apply tw-text-red-500;
        }

        &.is-verify {
          @apply tw-text-amber-500;
        }
      }
    }
  }
</style>

<style lang="scss">
  // Not scoped: v-menu teleports its content to the app root.
  .v2-native-printer-card-menu {
    @apply tw-rounded-xl tw-shadow-lg;

    .menu-panel {
      @apply tw-flex tw-min-w-[200px] tw-flex-col tw-bg-white tw-p-1;

      .menu-item {
        @apply tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-px-3 tw-py-2.5 tw-text-left tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-bg-neutral-50;

        &:disabled {
          @apply tw-cursor-not-allowed tw-opacity-50;
        }

        .menu-item-icon {
          @apply tw-text-lg tw-text-neutral-400;
        }

        &.is-danger {
          @apply tw-text-red-600;

          .menu-item-icon {
            @apply tw-text-red-400;
          }
        }
      }
    }
  }
</style>
